import { useRef } from 'react';
import { UploadedFile } from '../types';
import { validateFile } from '../../../common/utils';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { uploadFile } from '../../IdeationCOI/IdeationService';

interface UseFileUploadProps {
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  details: string;
  setDetails: React.Dispatch<React.SetStateAction<string>>;
  showSnackbar: (message: string, severity?: 'error' | 'success' | 'warning') => void;
  isSubmitting: boolean;
}

export const useFileUpload = ({
  setUploadedFiles,
  details,
  setDetails,
  showSnackbar,
  isSubmitting,
}: UseFileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFile = () => {
    if (isSubmitting) return;
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isSubmitting) return;
    const files = event.target.files;
    if (files) {
      const filesArray = Array.from(files);
      const validatedFiles: { file: File; index: number }[] = [];

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        const validation = await validateFile(file);

        if (!validation.ok) {
          ShowToast({
            message: validation?.error || 'File validation failed',
            type: ToastTypeEnum.Error,
          });
          setUploadedFiles((prev) => [
            ...prev,
            {
              id: `failed-${Date.now()}-${i}`,
              name: file.name,
              file,
              isUploading: false,
              isAnalyzing: false,
              failed: true,
              error: validation?.error,
            },
          ]);
          continue;
        } else {
          validatedFiles.push({ file, index: i });
        }
      }

      if (validatedFiles.length === 0) {
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      const tempFiles: UploadedFile[] = validatedFiles.map(({ file, index }) => ({
        id: `file-${Date.now()}-${index}`,
        name: file.name,
        file,
        isUploading: true,
      }));

      setUploadedFiles((prev) => [...prev, ...tempFiles]);

      for (let i = 0; i < tempFiles.length; i++) {
        const tempFile = tempFiles[i];
        try {
          const response = await uploadFile(tempFile.file);

          if (response && response.files && response.files.length > 0) {
            const fileUrl = response.files[0];
            const pageCount = response.total_page_count || 0;
            const isDocx = tempFile.name.toLowerCase().endsWith('.docx');

            if (isDocx && pageCount > 40) {
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.id === tempFile.id
                    ? {
                        ...f,
                        isUploading: false,
                        isAnalyzing: false,
                        failed: true,
                        error: 'DOCX cannot exceed 40 pages',
                      }
                    : f
                )
              );
              continue;
            }

            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.id === tempFile.id
                  ? {
                      ...f,
                      url: fileUrl,
                      isUploading: false,
                      isAnalyzing: false,
                      filePath: response.files[0],
                      fileSize: `${Math.round(tempFile.file.size / 1024)}KB`,
                    }
                  : f
              )
            );
          } else {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.id === tempFile.id
                  ? {
                      ...f,
                      isUploading: false,
                      isAnalyzing: false,
                      failed: true,
                    }
                  : f
              )
            );

            showSnackbar(`Upload failed for ${tempFile.name}`, 'error');
            console.error('File upload failed for:', tempFile.name);
          }
        } catch (error) {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === tempFile.id
                ? {
                    ...f,
                    isUploading: false,
                    isAnalyzing: false,
                    failed: true,
                  }
                : f
            )
          );

          showSnackbar(`Upload failed for ${tempFile.name}`, 'error');
          console.error('Error uploading file:', tempFile.name, error);
        }
      }
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    if (isSubmitting) return;
    if (!event.clipboardData) return;

    const items = event.clipboardData.items;
    if (!items) return;

    const imageItem = Array.from(items).find((item) => item.type.startsWith('image/'));

    if (imageItem) {
      event.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) return;

      const validation = await validateFile(file);
      if (!validation.ok) {
        showSnackbar(validation?.error || 'File validation failed', 'error');
        return;
      }

      const tempFile: UploadedFile = {
        id: `pasted-${Date.now()}`,
        name: file.name || 'pasted-image.png',
        file,
        isUploading: true,
      };

      setUploadedFiles((prev) => [...prev, tempFile]);

      try {
        const response = await uploadFile(file);

        if (response && response.files && response.files.length > 0) {
          const fileUrl = response.files[0];
          const pageCount = response.total_page_count || 0;
          const isDocx = tempFile.name.toLowerCase().endsWith('.docx');

          if (isDocx && pageCount > 40) {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.id === tempFile.id
                  ? {
                      ...f,
                      isUploading: false,
                      isAnalyzing: false,
                      failed: true,
                      error: 'DOCX cannot exceed 40 pages',
                    }
                  : f
              )
            );
            return;
          }

          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === tempFile.id
                ? {
                    ...f,
                    url: fileUrl,
                    isUploading: false,
                    isAnalyzing: false,
                    filePath: response.files[0],
                    fileSize: `${Math.round(file.size / 1024)}KB`,
                  }
                : f
            )
          );
        } else {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.id === tempFile.id
                ? {
                    ...f,
                    isUploading: false,
                    isAnalyzing: false,
                    failed: true,
                  }
                : f
            )
          );
          showSnackbar('Image paste upload failed', 'error');
        }
      } catch (error) {
        showSnackbar('Image paste upload failed', 'error');

        setUploadedFiles((prev) => prev.filter((f) => f.id !== tempFile.id));
      }

      return;
    }

    const pastedText = event.clipboardData.getData('text');

    if (pastedText) {
      event.preventDefault();

      // We need access to the input element to insert text at cursor position.
      // Since we don't have direct ref key to TextField input here easily, append or use state.
      // ProcessCreation logic used event.target.
      // Ideally we should pass the event handler logic back or use a ref passed from parent.
      // But event.target in handlePaste gives the element.

      const input = event.target as HTMLTextAreaElement;
      // Check if it is indeed a textarea/input
      if (input && (input.tagName === 'TEXTAREA' || input.tagName === 'INPUT')) {
          const start = input.selectionStart;
          const end = input.selectionEnd;
          const newValue = details.slice(0, start) + pastedText + details.slice(end);
          setDetails(newValue);
      } else {
          // Fallback if target is not input (unlikely for onPaste on input)
          setDetails(prev => prev + pastedText);
      }
    }
  };

  return {
    fileInputRef,
    handleAddFile,
    handleFileUpload,
    handleRemoveFile,
    handlePaste,
  };
};
